import { LearningExprienceItem } from './learning-experience-item';
import { Subtitle } from './subtitle';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dataConfig, useConfig } from '@/hooks/useConfig';
import {
  BookOpen,
  BookMarked,
  Calendar,
  MessageCircle,
  Clock,
  User,
  Building2,
  Route,
} from 'lucide-react';
import { getCardSx } from '@/theme/utils';

export const LearningExperience = ({
  coach,
  company,
  currentCourse,
  currentModule,
  dayToLearn,
  gatewayOptions,
  totalHours,
}: {
  currentCourse: string;
  currentModule: string;
  dayToLearn: string;
  gatewayOptions: string;
  totalHours: string;
  coach: string;
  company: string;
  changeDaysAndHours: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const config = useConfig();
  const { data: configData } = config as dataConfig;

  return (
    <Box sx={{ ...getCardSx(theme), cursor: 'default', mt: 2 }}>
      <Subtitle
        icon={<Route size={18} color={theme.palette.primary.main} />}
        text={
          configData?.custom_profile_sections?.student_profile_learning
            ? t('profileScreen.learningExperienceShort') +
              ` ${configData.custom_profile_sections.student_profile_learning}`
            : t('profileScreen.learningExperience')
        }
      />
      <LearningExprienceItem
        onClick={() => navigate('/change-module')}
        title={
          configData?.course_label
            ? configData.course_label
            : t(`profileScreen.currentCourse${config.isMeidei}`)
        }
        currentCourse={currentCourse}
        icon={<BookOpen size={20} color={theme.palette.primary.main} />}
      />
      <LearningExprienceItem
        onClick={() => navigate('/change-module')}
        title={
          config.data?.module_label
            ? config.data.module_label
            : t(`profileScreen.currentSkill${config.isMeidei}`)
        }
        currentCourse={currentModule}
        icon={<BookMarked size={20} color={theme.palette.primary.main} />}
      />
      <LearningExprienceItem
        onClick={() => navigate('/profile/change-day-and-time')}
        title={t('profileScreen.daysAndPreferredHours')}
        currentCourse={dayToLearn}
        icon={<Calendar size={20} color={theme.palette.primary.main} />}
      />
      <LearningExprienceItem
        onClick={() => navigate('/profile/gateway')}
        title={
          Boolean(configData?.custom_profile_sections?.student_profile_capsule)
            ? `${t('profileScreen.choiseGatewayShort')} ${
                configData.custom_profile_sections.student_profile_capsule
              }`
            : t(`profileScreen.choiseGateway${config.isMeidei}`)
        }
        currentCourse={gatewayOptions}
        icon={<MessageCircle size={20} color={theme.palette.primary.main} />}
      />
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: theme.fontSizes.caption,
          color: theme.palette.text.disabled,
          mb: 0.5,
          mt: 1,
        }}
      >
        {t('profileScreen.generalInfo')}
      </Typography>
      <LearningExprienceItem
        showIcon={false}
        opacity
        title={
          configData?.custom_profile_sections?.consumed_hours_profile_capsule
            ? configData.custom_profile_sections.consumed_hours_profile_capsule
            : t('profileScreen.consumedLearningHours')
        }
        currentCourse={totalHours}
        icon={<Clock size={20} color={theme.palette.text.disabled} />}
      />
      <LearningExprienceItem
        showIcon={false}
        opacity
        title={
          configData?.custom_profile_sections?.coach_profile_capsule
            ? configData.custom_profile_sections.coach_profile_capsule
            : t('profileScreen.coach')
        }
        currentCourse={coach}
        icon={<User size={20} color={theme.palette.text.disabled} />}
      />
      <LearningExprienceItem
        showIcon={false}
        opacity
        title={
          configData?.custom_profile_sections?.company_profile_capsule
            ? configData.custom_profile_sections.company_profile_capsule
            : t(`general.company${config.isMeidei}`)
        }
        currentCourse={company}
        icon={<Building2 size={20} color={theme.palette.text.disabled} />}
      />
    </Box>
  );
};
